"use client";

import { FormEvent, useState } from "react";
import { trackUkDomainHandoff } from "@/lib/analytics/domainHandoff";
import Image from "next/image";
import Link from "next/link";
import { BrandPattern } from "@/components/sa-design-system/BrandPattern";
import { GOOGLE_REVIEW_BASED_ON_LABEL, GOOGLE_REVIEW_RATING } from "@/lib/reviewProof";

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/singing-attitude-ltd", kind: "linkedin" as const },
  { label: "Instagram", href: "https://instagram.com/singing.attitude", kind: "instagram" as const },
  { label: "YouTube", href: "https://www.youtube.com/@liubadogassingingattitude8182", kind: "youtube" as const },
  { label: "Facebook", href: "https://www.facebook.com/singingattitude/", kind: "facebook" as const },
];

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[1.05rem] w-[1.05rem]">
      <path
        fill="currentColor"
        d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.85 1.55a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z"
      />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[1.05rem] w-[1.05rem]">
      <path
        fill="currentColor"
        d="M21.37 7.33a2.98 2.98 0 0 0-2.1-2.1C17.39 4.7 12 4.7 12 4.7s-5.39 0-7.27.53a2.98 2.98 0 0 0-2.1 2.1C2.1 9.21 2.1 12 2.1 12s0 2.79.53 4.67a2.98 2.98 0 0 0 2.1 2.1C6.61 19.3 12 19.3 12 19.3s5.39 0 7.27-.53a2.98 2.98 0 0 0 2.1-2.1c.53-1.88.53-4.67.53-4.67s0-2.79-.53-4.67ZM10.2 15.07V8.93L15.5 12l-5.3 3.07Z"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[1.05rem] w-[1.05rem]">
      <path
        fill="currentColor"
        d="M13.32 21v-7.76h2.6l.39-3.03h-2.99V8.28c0-.88.24-1.48 1.5-1.48h1.61V4.09c-.28-.04-1.24-.09-2.35-.09-2.33 0-3.92 1.42-3.92 4.03v2.18H7.52v3.03h2.64V21h3.16Z"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[1.05rem] w-[1.05rem]">
      <path
        fill="currentColor"
        d="M6.39 8.71V21H2.3V8.71h4.09Zm.27-3.8c0 1.18-.88 2.12-2.31 2.12H4.32c-1.37 0-2.3-.94-2.3-2.12 0-1.2.96-2.11 2.36-2.11s2.25.91 2.28 2.11ZM21.7 13.44V21h-4.08v-7.09c0-1.78-.64-3-2.24-3-1.22 0-1.95.83-2.27 1.62-.12.29-.15.7-.15 1.1V21H8.87s.06-10.1 0-11.15h4.09v1.58c.54-.84 1.51-2.04 3.68-2.04 2.69 0 4.71 1.76 4.71 5.54Z"
      />
    </svg>
  );
}

const footerIconPaths = {
  calendar: "M7 3v3M17 3v3M4.5 9h15M6.5 5h11A2.5 2.5 0 0 1 20 7.5v10A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-10A2.5 2.5 0 0 1 6.5 5Z",
  heart: "M20.4 5.8a5 5 0 0 0-7.1 0L12 7.1l-1.3-1.3a5 5 0 0 0-7.1 7.1L12 21l8.4-8.1a5 5 0 0 0 0-7.1Z",
  lock: "M7.5 10V7.6a4.5 4.5 0 0 1 9 0V10M6.5 10h11A1.5 1.5 0 0 1 19 11.5v7A1.5 1.5 0 0 1 17.5 20h-11A1.5 1.5 0 0 1 5 18.5v-7A1.5 1.5 0 0 1 6.5 10Z",
  map: "M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Zm0-8.2A2.8 2.8 0 1 0 12 7a2.8 2.8 0 0 0 0 5.8Z",
  message: "M5.5 5.5h13v9h-7L7 18.5v-4h-1.5v-9Z",
  shield: "M12 22s7-3.6 7-10V5l-7-3-7 3v7c0 6.4 7 10 7 10Z",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 21a7.5 7.5 0 0 1 15 0",
} as const;

type FooterIconName = keyof typeof footerIconPaths;

function FooterIcon({ name, className = "h-5 w-5" }: { name: FooterIconName; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={footerIconPaths[name]} />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 5h5v5M10 14 19 5M19 14v4.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 .5-.5H10" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.19 3.32v2.76h3.55c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.24 1.05-3.73 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.85A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.1A6.61 6.61 0 0 1 5.5 12c0-.73.12-1.43.34-2.1V7.05H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.95l3.66-2.85Z" />
      <path fill="#EA4335" d="M12 5.37c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.57 10.57 0 0 0 12 1 11 11 0 0 0 2.18 7.05L5.84 9.9c.87-2.6 3.3-4.53 6.16-4.53Z" />
    </svg>
  );
}

const footerLinks = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms & Conditions", href: "/terms" },
  { label: "Cookies Policy", href: "/cookies" },
  { label: "Lesson Terms", href: "/terms" },
];

const localLessonLinks = [
  { label: "Local lessons", href: "/local-lessons" },
  { label: "Group singing classes", href: "/group-singing-classes" },
  { label: "Singing lessons Ely", href: "/singing-lessons-ely" },
  { label: "Singing lessons Bury St Edmunds", href: "/singing-lessons-bury-st-edmunds" },
  { label: "Singing Lessons in Cambridge, UK", href: "https://www.singingattitude.co.uk/" },
  { label: "Current local lesson status", href: "/local-lessons#availability" },
  { label: "About Liuba", href: "/about-liuba" },
  { label: "Student success stories", href: "/success-stories" },
  { label: "UK blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const studioLinks = [
  { label: "Ely / Soham Studio", href: "/singing-lessons-ely" },
  { label: "Bury St Edmunds Studio", href: "/singing-lessons-bury-st-edmunds" },
  { label: "Local lesson information", href: "/local-lessons" },
  { label: "Current availability", href: "/local-lessons#availability" },
];

const moreFromSingingAttitudeLinks = [
  { label: "Founder story", href: "https://singingattitude.com/founder-story" },
  { label: "Student success stories / Proof", href: "https://singingattitude.com/proof" },
  { label: "Online singing evaluation", href: "https://singingattitude.com/evaluation" },
  { label: "Video feedback", href: "https://singingattitude.com/video-feedback" },
];

const trustPoints = [
  {
    title: "Two local studios",
    copy: "Ely / Soham and Bury St Edmunds",
    icon: "map" as const,
  },
  {
    title: "Private 1-to-1 lessons",
    copy: "Tailored to your voice",
    icon: "user" as const,
  },
  {
    title: "Clear booking process",
    copy: "Your exact appointment is confirmed separately",
    icon: "message" as const,
  },
  {
    title: "Supportive environment",
    copy: "For adults and beginners",
    icon: "heart" as const,
  },
];

const resourceCards = [
  {
    title: "Online evaluation",
    copy: "Voice analysis",
    href: "https://singingattitude.com/evaluation",
    image: "/images/sa-com-pics/LiubaStrippy.avif",
    imagePosition: "50% 38%",
  },
  {
    title: "Video feedback",
    copy: "Personal feedback",
    href: "https://singingattitude.com/video-feedback",
    image: "/images/sa-com-pics/Mic.webp",
    imagePosition: "48% 50%",
  },
  {
    title: "Founder story",
    copy: "Meet Liuba Doga",
    href: "https://singingattitude.com/founder-story",
    image: "/images/sa-com-pics/liuba-doga-unesco-award.avif",
    imagePosition: "50% 34%",
  },
];

const reassuranceItems = [
  {
    title: "No pressure. No audition.",
    icon: "shield" as const,
  },
  {
    title: "Adults and beginners welcome.",
    icon: "user" as const,
  },
  {
    title: "Local lesson information for Ely / Soham and Bury St Edmunds.",
    icon: "calendar" as const,
  },
  {
    title: "We’re here to help.",
    icon: "message" as const,
  },
];

function FooterNewsletterSignup({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setStatus("error");
      setMessage("Enter your email address.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          consent: true,
          lang: "en",
          leadType: "newsletter",
          source: "local_singing_tips",
          context: "uk_footer_newsletter",
          pagePath: typeof window !== "undefined" ? window.location.pathname : "/",
          metadata: {
            newsletter: "local_singing_tips",
            source_page: "footer",
            source_section: "site_footer",
            source_cta: "footer_newsletter",
          },
        }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error || "Unable to subscribe right now.");
      }

      setEmail("");
      setStatus("success");
      setMessage("You're subscribed.");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Unable to subscribe right now.");
    }
  }

  const content = (
    <>
      <div className={compact ? "space-y-1.5" : ""}>
        <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#d9a93d]">Singing tips</p>
        <h2 className={compact ? "font-serif text-xl font-semibold leading-tight tracking-tight" : "mt-2 font-serif text-2xl font-semibold leading-tight tracking-tight"}>
          Get local singing tips by email
        </h2>
        <p className={compact ? "max-w-md text-xs leading-4 text-[#fffaf2]/68" : "mt-2 max-w-2xl text-sm leading-5 text-[#fffaf2]/72"}>
          Helpful notes for adult singers, plus occasional updates about local lesson availability.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="grid gap-2" noValidate>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto]">
          <label className="sr-only" htmlFor="footer-newsletter-email">
            Email address
          </label>
          <input
            id="footer-newsletter-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email address"
            autoComplete="email"
            className="min-h-10 rounded-[9px] border border-white/22 bg-white/[0.08] px-3 text-sm font-semibold text-[#fffaf2] outline-none transition placeholder:text-[#fffaf2]/45 focus:border-[#d9a93d] focus:bg-white/[0.12]"
            aria-describedby={message ? "footer-newsletter-status" : undefined}
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex min-h-10 items-center justify-center rounded-[9px] bg-[#d9a93d] px-3 text-sm font-black text-[#102f2a] transition hover:bg-[#e2b859] disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d9c78f]"
          >
            {status === "loading" ? "Subscribing..." : "Subscribe"}
          </button>
        </div>
        {message ? (
          <p
            id="footer-newsletter-status"
            className={`text-xs font-semibold ${status === "error" ? "text-[#ffd6c9]" : "text-[#dff7e7]"}`}
            aria-live="polite"
          >
            {message}
          </p>
        ) : null}
        <p className="text-[0.68rem] leading-4 text-[#fffaf2]/52">
          Unsubscribe any time. Used only for Singing Attitude updates.
        </p>
      </form>
    </>
  );

  if (compact) {
    return (
      <div className="grid gap-3 rounded-[14px] border border-white/14 bg-white/[0.055] p-3 shadow-[0_10px_24px_rgba(5,17,15,0.08)]">
        {content}
      </div>
    );
  }

  return (
    <section className="rounded-[18px] border border-[#d9c78f]/40 bg-[#0d3f36] p-4 text-[#fffaf2] shadow-[0_12px_30px_rgba(8,43,37,0.12)] sm:p-5">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(20rem,1fr)] lg:items-end">
        {content}
      </div>
    </section>
  );
}

export function SiteFooter() {
  const year = new Date().getFullYear();
  const openCookiePreferences = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event("sa:cookie-preferences-open"));
  };

  return (
    <footer className="relative border-t border-[#d9cdb6] bg-[#fffaf2] text-[#13211f]">
      <BrandPattern className="opacity-10" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_0%,rgba(217,199,143,0.2),transparent_28rem),radial-gradient(circle_at_90%_10%,rgba(19,59,52,0.08),transparent_30rem)]" />
      <div className="relative mx-auto flex w-full max-w-[92rem] flex-col gap-4 px-4 py-5 text-center text-sm sm:px-6 sm:text-left lg:px-8 lg:py-6">
        <section className="overflow-hidden rounded-[16px] border border-[#d9c78f]/30 bg-[#063d35] text-[#fffaf2] shadow-[0_12px_34px_rgba(8,43,37,0.16)] ring-1 ring-white/10">
          <div className="grid lg:grid-cols-[13rem_minmax(0,1fr)]">
            <figure className="relative min-h-[8rem] overflow-hidden lg:min-h-full">
              <Image
                src="/images/local-lessons/ely/ely-studio-liuba-doga.jpg"
                alt="Liuba Doga in the Ely Studio teaching space"
                fill
                sizes="(min-width: 1024px) 20rem, 100vw"
                className="object-cover object-[62%_center]"
              />
              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(6,61,53,0)_55%,#063d35_100%),linear-gradient(180deg,rgba(6,61,53,0.06),rgba(6,61,53,0.16))]" aria-hidden />
            </figure>
            <div className="grid gap-3 p-3 sm:p-4 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.02fr)_minmax(18rem,0.86fr)] lg:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-2xl font-semibold leading-tight tracking-tight lg:text-[1.85rem]">
                  Find your first lesson
                </h2>
                <p className="mt-1.5 max-w-xl text-sm font-medium leading-5 text-[#fffaf2]/78">
                  Start with one private lesson and understand your voice. Compare prices, choose your studio, and see the booking steps.
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
                  <Link
                    href="/local-lessons#buy"
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-[9px] bg-[#d9a93d] px-3 text-sm font-black text-[#102f2a] shadow-[0_10px_22px_rgba(5,17,15,0.18)] transition hover:-translate-y-0.5 hover:bg-[#e2b859] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d9c78f]"
                  >
                    <FooterIcon name="calendar" className="h-[1.125rem] w-[1.125rem]" />
                    See prices & book
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-[9px] border border-white/35 bg-white/[0.06] px-3 text-sm font-black text-[#fffaf2] transition hover:-translate-y-0.5 hover:border-[#d9c78f] hover:bg-white/[0.1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d9c78f]"
                  >
                    Ask a question
                  </Link>
                </div>
              </div>
              <div className="grid gap-2 border-t border-white/16 pt-3 sm:grid-cols-2 lg:border-l lg:border-t-0 lg:pl-3 lg:pt-0">
                {trustPoints.map((item) => (
                  <div key={item.title} className="min-w-0 text-center sm:text-left">
                    <span className="mx-auto flex h-7 w-7 items-center justify-center rounded-full border border-[#d9a93d]/65 text-[#d9a93d] sm:mx-0">
                      <FooterIcon name={item.icon} className="h-4 w-4" />
                    </span>
                    <p className="mt-1.5 text-xs font-black leading-tight text-[#fffaf2]">{item.title}</p>
                    <p className="mt-0.5 text-[0.72rem] leading-4 text-[#fffaf2]/64">{item.copy}</p>
                  </div>
                ))}
              </div>
              <FooterNewsletterSignup compact />
            </div>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-[0.95fr_3.15fr]">
          <div>
            <div className="flex items-start justify-center gap-3 sm:justify-start">
              <Link href="/" className="inline-flex shrink-0" aria-label="Singing Attitude UK home">
                <Image
                  src="/images/sa-com-pics/SA_LOGO-transparent.png"
                  alt="Singing Attitude UK logo"
                  width={736}
                  height={844}
                  className="h-auto w-16 drop-shadow-[0_8px_18px_rgba(151,115,46,0.14)]"
                  sizes="80px"
                />
              </Link>
              <div className="pt-1">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <span className="flex h-8 w-8 items-center justify-center" role="img" aria-label="Google">
                    <GoogleIcon />
                  </span>
                  <span className="text-lg font-black leading-none text-[#123b34]">{GOOGLE_REVIEW_RATING}</span>
                  <span className="inline-flex gap-0.5 text-[#d9a93d]" role="img" aria-label={`${GOOGLE_REVIEW_RATING} Google rating`}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <svg key={index} viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5" fill="currentColor">
                        <path d="m12 2.9 2.8 5.7 6.3.9-4.6 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2-4.6-4.4 6.3-.9L12 2.9Z" />
                      </svg>
                    ))}
                  </span>
                </div>
                <p className="mt-1 text-xs font-semibold leading-4 text-[#5a6a65]">
                  {GOOGLE_REVIEW_BASED_ON_LABEL}.
                </p>
              </div>
            </div>
            <h2 className="mt-3 text-lg font-black uppercase leading-tight tracking-[0.04em] text-[#123b34]">
              Singing Attitude UK
            </h2>
            <p className="mt-1 text-xs font-bold leading-4 text-[#6d6252]">
              Operated by Singing Attitude Ltd · Company no. 16984284.
            </p>
            <p className="mt-2 max-w-sm text-sm leading-5 text-[#42524d]">
              Private singing lessons in Ely / Soham and Bury St Edmunds with Liuba Doga.
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              {socialLinks.map((social) => {
                const Icon =
                  social.kind === "linkedin"
                    ? LinkedInIcon
                    : social.kind === "instagram"
                    ? InstagramIcon
                    : social.kind === "youtube"
                      ? YouTubeIcon
                      : FacebookIcon;
                return (
                  <Link
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${social.label} (opens in a new tab)`}
                    title={social.label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#ded3bc] bg-white/52 text-[#0d3f36] transition hover:border-[#8b6a2e]/45 hover:bg-white/86 hover:text-[#13211f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald/70"
                  >
                    <Icon />
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.08fr_1.16fr]">
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.26em] text-[#8b6a2e]">Local lessons</h3>
              <ul className="mt-2.5 space-y-1">
                {localLessonLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className="inline-flex min-h-8 items-center text-sm font-semibold leading-5 text-[#263d38] transition hover:text-[#8b6a2e]">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.26em] text-[#8b6a2e]">Studios</h3>
              <ul className="mt-2.5 space-y-1">
                {studioLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className="inline-flex min-h-8 items-center text-sm font-semibold leading-5 text-[#263d38] transition hover:text-[#8b6a2e]">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.26em] text-[#8b6a2e]">More from SingingAttitude.com</h3>
              <ul className="mt-2.5 space-y-1">
                {moreFromSingingAttitudeLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      onClick={(event) => { if (!event.defaultPrevented) trackUkDomainHandoff(event.currentTarget.href, window.location.pathname); }}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-8 items-center gap-2 text-sm font-semibold leading-5 text-[#263d38] transition hover:text-[#8b6a2e]"
                    >
                      {link.label}
                      <ExternalIcon />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.26em] text-[#8b6a2e]">Continue your singing journey</h3>
              <div className="mt-2.5 grid gap-2">
                {resourceCards.map((card) => (
                  <Link
                    key={card.href}
                    href={card.href}
                    onClick={(event) => { if (!event.defaultPrevented) trackUkDomainHandoff(event.currentTarget.href, window.location.pathname); }}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative grid grid-cols-1 rounded-[10px] border border-[#ded3bc] bg-white/52 p-1.5 shadow-[0_6px_16px_rgba(49,39,24,0.03)] transition hover:-translate-y-0.5 hover:border-[#d9a93d]/55 hover:bg-white/86 sm:grid-cols-[3.9rem_minmax(0,1fr)] sm:gap-2.5"
                  >
                    <span className="absolute inset-y-1.5 left-1.5 w-[3.9rem] overflow-hidden rounded-[8px] bg-[#e7dac4] sm:relative sm:inset-auto sm:min-h-[3.15rem] sm:w-auto">
                      <Image
                        src={card.image}
                        alt=""
                        fill
                        sizes="62px"
                        className="object-cover"
                        style={{ objectPosition: card.imagePosition }}
                      />
                    </span>
                    <span className="flex min-h-[3.15rem] min-w-0 flex-col justify-center px-[4.4rem] py-0.5 text-center sm:min-h-0 sm:px-0 sm:text-left">
                      <span className="block text-sm font-black leading-tight text-[#123b34] group-hover:text-[#8b6a2e]">{card.title}</span>
                      <span className="mt-0.5 block text-xs font-medium leading-4 text-[#5a6a65]">{card.copy}</span>
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                href="https://singingattitude.com"
                target="_blank"
                rel="noreferrer"
                className="mt-2.5 inline-flex items-center justify-center gap-2 text-sm font-black text-[#123b34] underline decoration-[#d9a93d]/45 underline-offset-4 transition hover:text-[#8b6a2e] sm:justify-start"
              >
                Visit singingattitude.com
                <ExternalIcon />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid gap-3 border-y border-[#ded3bc] py-2.5 md:grid-cols-2 xl:flex xl:items-center xl:justify-between xl:gap-6">
          {reassuranceItems.map((item, index) => {
            const alignmentClass =
              index === reassuranceItems.length - 1 ? "xl:justify-end" : index > 0 ? "xl:justify-center" : "";

            return (
              <div key={item.title} className={`flex min-w-0 items-center justify-center gap-2.5 md:justify-start ${alignmentClass}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#d9a93d]/40 bg-[#f7ebc8]/70 text-[#9a6a18]">
                  <FooterIcon name={item.icon} className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-black leading-4 text-[#123b34] sm:text-sm">{item.title}</span>
                </span>
              </div>
            );
          })}
        </div>

        <p className="text-xs leading-4 text-[#6d6252]">
          <span className="font-black uppercase tracking-[0.18em] text-[#8b6a2e]">Studio information:</span>{" "}
          See our local lesson and studio pages for current prices, lesson options and appointment arrangements. Article authors and publication dates are shown on each guide.
        </p>

        <div className="flex flex-col gap-3 text-xs text-[#6d6252] lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p>© {year} Singing Attitude Ltd. Registered office: 17 Avocet Grove, Soham, Ely, England, CB7 5GN. All rights reserved.</p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start sm:gap-4">
              {footerLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="inline-flex items-center py-2 font-semibold text-[#42524d] transition hover:text-[#8b6a2e]"
                >
                  {link.label}
                </Link>
              ))}
              <button type="button" onClick={openCookiePreferences} className="inline-flex items-center py-2 font-semibold text-[#42524d] transition hover:text-[#8b6a2e]">
                Cookie Preferences
              </button>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-center lg:self-end">
            <Image
              src="/images/method/liuba-doga-signature.png"
              alt="Liuba Doga"
              width={1109}
              height={419}
              className="h-auto w-32 opacity-75"
              sizes="128px"
            />
            <span className="text-[1.75rem] leading-none text-[#d9a93d]" aria-hidden="true">
              ♥
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
